import { b64toBlob } from '../constants.js/orders';
import { axiosInstance } from './instance';
import { getStoreService } from './store';

const getProductsServiceMobile = (storeId, limit = 999) => {
    return axiosInstance.get(`/stores/${storeId}/products?limit=${limit}&sort=name`);
};

/**
 * Gets the products list with combinable filters.
 * @param {number} storeId Store ID
 * @param {number} limit Number of items per page
 * @param {number} page Page number (0-based)
 * @param {string} filterName Search keyword for name or SKU
 * @param {number|null} filterStatus Product status (1 = active, 0 = inactive, null = all)
 * @param {string|null} sortField Sort field ('name', 'price', or null for default)
 * @param {string|null} sortOrder Sort direction ('asc', 'desc', or null for default)
 * @param {string|number|null} collectionId Collection / subcategory ID
 */
const getProductsService = (
    storeId,
    limit = 10,
    page = 0,
    filterName = '',
    filterStatus = null,
    sortField = null,
    sortOrder = null,
    collectionId = null
) => {
    const params = [`limit=${limit}`, `page=${page + 1}`];

    if (
        sortField &&
        (sortOrder === 'asc' || sortOrder === 'desc')
    ) {
        params.push(`sort=${sortField} ${sortOrder}`);
    }

    const trimmedFilter = (filterName || '').trim();
    if (trimmedFilter) {
        params.push(`name.contains=${encodeURIComponent(trimmedFilter)}`);
        params.push(`sku.contains=${encodeURIComponent(trimmedFilter)}`);
    }

    if (filterStatus !== null && filterStatus !== undefined) {
        params.push(`active.equals=${filterStatus}`);
    }

    if (collectionId) {
        params.push(`product_collection.collection_id.equals=${collectionId}`);
    }

    const query = params.join('&');
    return axiosInstance.get(`/stores/${storeId}/products?${query}`);
};

const getDownloadProducts = (storeId) => {
    return axiosInstance
        .get(`/stores/${storeId}/products-download`, { timeout: 5 * 60 * 1000 })
        .then((response) => {
            const base64Content = response.data.base64Content;

            const blob = b64toBlob(base64Content);

            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;

            const fileName = 'productos.xlsx';
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();

            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);
        })
        .catch((error) => {
            console.error('Error al descargar el archivo:', error);
        });
};

/**
 * Returns true when the store is Freemium and has reached (or exceeded) the products limit.
 * @param {Object} storeData full store object (must include store_plan / storePlanStatus)
 * @param {number} storeId store id
 * @param {number} limit max allowed products for Freemium (default 20)
 * @returns {Promise<boolean>}
 */
const isFreemiumPlanAndProductsReachedLimit = (storeId, limit = 20) => {
    if (!storeId) {
        return Promise.resolve(false);
    }

    return getStoreService(storeId)
        .then(({ data: storeData }) => {
            const isFreemiumPlan =
                storeData?.store_plan?.plan?.name?.toLowerCase() === 'freemium' ||
                String(storeData?.store_plan?.plan?.id ?? '') === '44' ||
                String(storeData?.storePlanStatus?.id ?? '') === '44';

            if (!isFreemiumPlan) {
                return false;
            }

            return getProductsService(storeId, 1, 0).then((res) => {
                const totalProducts =
                    Number(res.headers['x-total-count']) ||
                    (Array.isArray(res.data) ? res.data.length : 0);
                return totalProducts >= limit;
            });
        })
        .catch(() => false);
};

/**
 * Creates a product
 * @param {number} storeId store id
 * @param {Object} data product data
 */
const createProductService = (storeId, data) => {
    return axiosInstance.post(`/stores/${storeId}/products`, data);
};

const updateProductServiceAdditionalInfo = (storeId, productId, data) => {
    return axiosInstance.put(
        `/stores/${storeId}/products/${productId}/additional-info/${data.id}`,
        data
    );
};

const deleteProductServiceAdditionalInfo = (storeId, productId, data) => {
    return axiosInstance.delete(
        `/stores/${storeId}/products/${productId}/additional-info/${data.id}`,
        data
    );
};

const createProductServiceAdditionalInfo = (storeId, productId, data) => {
    return axiosInstance.post(`/stores/${storeId}/products/${productId}/additional-info`, data);
};

const getAdditionalInfo = (storeId, productId) => {
    return axiosInstance.get(`/stores/${storeId}/products/${productId}/additional-info`);
};

const deleteBulkProduct = (storeId, productIds) => {
    return axiosInstance.delete(`/stores/${storeId}/products`, { data: { ids: productIds } });
};

const bulkEnableProducts = (storeId, productIds) => {
    return axiosInstance.patch(`/stores/${storeId}/products/enable`, {
        ids: productIds.map(String),
    });
};

const bulkDisableProducts = (storeId, productIds) => {
    return axiosInstance.patch(`/stores/${storeId}/products/disable`, {
        ids: productIds.map(String),
    });
};

/**
 * Updates images for a products
 * @param {number} storeId store id
 * @param {number} productId product id
 * @param {Array} files array of files to upload
 */
const createProductImagesService = (storeId, productId, files) => {
    const formData = new FormData();
    files.forEach((file) => {
        formData.append('files', file);
    });
    return axiosInstance.put(`/stores/${storeId}/products/${productId}/images`, formData);
};

/**
 * Updates images for a product
 * @param {number} storeId store id
 * @param {number} processId process id
 * @param {Array} files array of files to upload
 */
const createBulkProductImagesService = (storeId, processId, sku, files) => {
    const formData = new FormData();
    files.forEach((file) => {
        formData.append('files', file);
    });
    return axiosInstance.put(
        `/stores/${storeId}/products/bulk/${processId}/images/${sku}`,
        formData
    );
};

/**
 * Updates images for a products
 * @param {number} storeId store id
 * @param {number} productId product id
 * @param {number} imageId image id
 */
const deleteProductImagesService = (storeId, productId, imageId) => {
    return axiosInstance.delete(`/stores/${storeId}/products/${productId}/images/${imageId}`);
};

/**
 * Updates images for a products
 * @param {number} storeId store id
 * @param {number} process process id
 * @param {number} imageId image id
 */
const deleteBulkProductImagesService = (storeId, processId, imageId) => {
    return axiosInstance.delete(`/stores/${storeId}/products/bulk/${processId}/images/${imageId}`);
};

/**
 * Updates a product
 * @param {number} storeId store id
 * @param {Object} data updated product data
 * @param {number} productId product id
 * @param {boolean} patch use the patch method
 */
const updateProductService = (storeId, data, productId, patch = false) => {
    if (patch) {
        return axiosInstance.patch(`/stores/${storeId}/products/${productId}`, data);
    }
    // v2 keeps option, option value and variant ids when they are sent back.
    return axiosInstance.put(`/stores/${storeId}/products/${productId}/v2`, data);
};

/**
 * Upload batch images for products via a zip file
 * @param {string} storeId store id
 * @param {File} file zipped file containing product images
 */
const uploadBatchProductImages = (storeId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axiosInstance.post(`/stores/${storeId}/products/images/bulk`, formData);
};

/**
 * Gets the information of a product
 * @param {number} storeId store id
 * @param {number} productId product id
 */
const getProductService = (storeId, productId) => {
    return axiosInstance.get(`/stores/${storeId}/products/${productId}`);
};

/**
 * deletes a product
 * @param {number} storeId store id
 * @param {number} productId product id
 */
const deleteProductService = (storeId, productId) => {
    return axiosInstance.delete(`/stores/${storeId}/products/${productId}`);
};

/**
 * Upload File Masive  product
 * @param {number} storeId store id
 * @param {number} productId product id
 */
const uploadBulkProducts = (storeId, value) => {
    return axiosInstance.post(`/stores/${storeId}/products/bulk`, value);
};

/**
 * Saves the products batch
 * @param {number} storeId store id
 * @param {array} items items array
 * @param {number} processId process id
 */
const saveBulkProducts = (storeId, items, processId) => {
    return axiosInstance.put(`/stores/${storeId}/products/bulk/${processId}/process`, items);
};

const saveAttachment = (storeId, id, value) => {
    return axiosInstance.put(`/stores/${storeId}/products/${id}/attachments`, value);
};

const removeAttachmentProduct = (storeId, id, attachment_id) => {
    return axiosInstance.delete(
        `/stores/${storeId}/products/${id}/attachments/${attachment_id}`,
        {}
    );
};

/**
 * Gets the products list
 * @param {number} storeId store id
 */
const getProductsOrder = (storeId, filterName = '') => {
    const term = encodeURIComponent(String(filterName ?? '').trim());
    if (!term) {
        return Promise.resolve({ data: [] });
    }
    return axiosInstance.get(
        `/stores/${storeId}/products?limit=50&sort=name&name.contains=${term}&sku.contains=${term}`
    );
};

const extractSearchResponseProducts = (response) => {
    const payload = response?.data;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.products)) return payload.products;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
};

const mergeProductsById = (lists) => {
    const map = new Map();
    lists.flat().forEach((product) => {
        if (product?.id != null) {
            map.set(String(product.id), product);
        }
    });
    return Array.from(map.values());
};

/**
 * Order product lookup: `/products-search` (name, SKU, variants) plus `/products` SKU filter.
 * @param {string} storeId store id
 * @param {string} filterName search term (name or SKU)
 */
const searchProductsForOrder = async (storeId, filterName = '') => {
    const term = String(filterName ?? '').trim();
    if (!term) {
        return { data: [] };
    }

    const encoded = encodeURIComponent(term);
    const requests = [
        axiosInstance.get(`/stores/${storeId}/products-search?term=${encoded}`),
        axiosInstance.get(
            `/stores/${storeId}/products?limit=50&sort=name&name.contains=${encoded}&sku.contains=${encoded}`
        ),
    ];

    if (/^\d+$/.test(term)) {
        requests.push(
            axiosInstance.get(`/stores/${storeId}/products?limit=10&sku.equals=${encoded}`)
        );
    }

    const results = await Promise.allSettled(requests);

    const lists = results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => extractSearchResponseProducts(result.value));

    return { data: mergeProductsById(lists) };
};

/**
 * Search products for orders (name, SKU, variants).
 * @param {string} storeId store id
 * @param {string} filterName search term (name or SKU)
 */
const getProducts = (storeId, filterName = '') => {
    return searchProductsForOrder(storeId, filterName);
};

/**
 * Gets the products list with variations
 * @param {string} storeId store id
 * @param {string} productId product id
 * @param {Array<string>} images the images to reorder
 */
const reorderProductImages = (storeId, productId, images) => {
    return axiosInstance.put(`/stores/${storeId}/products/${productId}/images/order`, images);
};

export {
    createBulkProductImagesService,
    createProductImagesService,
    createProductService,
    createProductServiceAdditionalInfo,
    deleteBulkProductImagesService,
    deleteProductImagesService,
    deleteProductService,
    deleteProductServiceAdditionalInfo,
    getAdditionalInfo,
    getProductService,
    getProducts,
    getProductsOrder,
    searchProductsForOrder,
    getProductsService,
    getProductsServiceMobile,
    removeAttachmentProduct,
    saveAttachment,
    saveBulkProducts,
    updateProductService,
    updateProductServiceAdditionalInfo,
    uploadBulkProducts,
    uploadBatchProductImages,
    getDownloadProducts,
    reorderProductImages,
    deleteBulkProduct,
    bulkEnableProducts,
    bulkDisableProducts,
    isFreemiumPlanAndProductsReachedLimit,
}
